import { useMemo } from 'react';

import { type AirActionTrackingLocation, type TrackClickedConnectToSlackOptionParams } from '@air/analytics';
import { type Board } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { EditBoardModal } from '~/components/EditPrivateBoardModal/EditBoardModal/EditBoardModal';
import { useConnectToSlack } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/hooks/useConnectToSlack';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useBoardSlackIntegrations } from '~/swr-hooks/slack/useBoardSlackIntegrations';
import { useSubscription } from '~/swr-hooks/subscriptions/useSubscription';
import { canChangeBoardIntegrationDetails } from '~/utils/permissions/boardPermissions';

export const useSlackMenuOptions = (board?: Board) => {
  const [showEditBoardModal] = useAirModal(EditBoardModal);
  const [showPlansModal] = useAirModal(PlansModal);
  const { data } = useBoardSlackIntegrations(board?.id);
  const { connectToSlack } = useConnectToSlack();
  const { boardPermissions } = useBoardPermissions({ boardId: board?.id });
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { data: subscription } = useSubscription();
  const { showToast } = useToasts();

  const canManageSlackIntegration = canChangeBoardIntegrationDetails({
    workspaceContext: workspacePermissions,
    boardContext: boardPermissions,
  });

  const onConnectToSlack = useMemo(
    () =>
      board && data?.length === 0 && canManageSlackIntegration
        ? async (trackLocation: TrackClickedConnectToSlackOptionParams['trackLocation']) => {
            if (!subscription?.isOnProPlan) {
              showPlansModal();
              return;
            }
            try {
              await connectToSlack({ board, trackLocation });
            } catch {
              showToast('There was an error with slack authentication. Please try again later', {
                timeBeforeAutoDismiss: undefined,
                type: 'assertive',
                withCloseButton: true,
              });
            }
          }
        : undefined,
    [data, canManageSlackIntegration, board, connectToSlack, subscription?.isOnProPlan, showPlansModal, showToast],
  );

  const onConnectedToSlack = useMemo(
    () =>
      board && !!data?.[0]
        ? (trackLocation: AirActionTrackingLocation) => {
            showEditBoardModal({ board, trackLocation });
          }
        : undefined,
    [data, board, showEditBoardModal],
  );

  return {
    onConnectToSlack,
    onConnectedToSlack,
    slackIntegrationData: board && !!data ? data[0] : undefined,
    isOnProPlan: subscription?.isOnProPlan,
  };
};
